import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useState, useCallback } from "react";
import { View, TouchableOpacity, StyleSheet, Platform } from "react-native";
import ColorPickerModal from "./ColorPickerModal";

const ColorPickerField = ({
  name,
  label,
  placeholder = "Hex color (e.g. #5d94d6)",
  showTextInputLabel = true,
  wrapperInputStyle,
  textContainer,
  errorOffset,
  onColorChange,
  initialColor,
  previewColor,
  previewTop,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { values, setFieldValue } = AppForms.useFormikContext();
  const currentValue = values?.[name] || "";

  const handleColorSelect = useCallback(
    (color) => {
      setFieldValue(name, color);
      onColorChange?.(color);
    },
    [name, setFieldValue, onColorChange],
  );

  const handleOpenModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const isValidHex = (hex) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
  };

  const hasValue = currentValue && currentValue.trim() !== "";
  const isValidValue = isValidHex(currentValue);

  const displayColor = hasValue
    ? isValidValue
      ? currentValue
      : "transparent"
    : previewColor || "transparent";

  const modalInitialColor = hasValue && isValidValue
    ? currentValue
    : previewColor || initialColor || "#000000";

  const styles = StyleSheet.create({
    container: {
      position: "relative",
    },
    colorPreviewContainer: {
      position: Platform.OS === "web" ? "absolute" : "relative",
      right: 10,
      top: Platform.OS === "web" ? (previewTop ?? 48) : 0,
      zIndex: 1,
    },
    colorPreview: {
      width: 24,
      height: 24,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
    },
  });

  return (
    <View style={styles.container}>
      <AppForms.AppFormField
        showTextInputLabel={showTextInputLabel}
        wrapperInputStyle={wrapperInputStyle}
        textContainer={textContainer}
        errorOffset={errorOffset}
        label={label}
        placeholder={placeholder}
        keyboardtype="default"
        textContextType="label"
        autoCorrect={false}
        name={name}
      />
      <View style={styles.colorPreviewContainer}>
        <TouchableOpacity
          style={[styles.colorPreview, { backgroundColor: displayColor }]}
          onPress={handleOpenModal}
          accessibilityLabel="Open color picker"
          accessibilityRole="button"
        />
      </View>
      <ColorPickerModal
        isVisible={isModalVisible}
        setIsModalOpen={setIsModalVisible}
        onClose={handleCloseModal}
        initialColor={modalInitialColor}
        onColorSelect={handleColorSelect}
      />
    </View>
  );
};

export default ColorPickerField;
